import confetti from 'canvas-confetti';
import { API } from './api';

/** Small in-memory cache so we hit the backend once per (kind × idx ×
 *  language × voice_gender) tuple across a whole browsing session. Values
 *  are `Blob` objects — we build a fresh Object URL on each play so the
 *  <audio> element can be reused. */
const audioCache = new Map();

async function fetchMilestoneAudio({ kind, idx, language, voiceGender }) {
  const key = `${kind}|${idx}|${language}|${voiceGender}`;
  const cached = audioCache.get(key);
  if (cached) return cached;
  try {
    const resp = await fetch(`${API}/milestones/audio`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind, idx, language, voice_gender: voiceGender }),
    });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    audioCache.set(key, blob);
    return blob;
  } catch { return null; }
}

// Client keeps a rotating index per (kind × language) so daily users
// hear a different phrase each milestone in the same session.
const rotation = new Map();
function nextIdx(kind, language) {
  const k = `${kind}|${language}`;
  const cur = rotation.get(k) ?? Math.floor(Math.random() * 5);
  rotation.set(k, cur + 1);
  return cur % 5;   // pool size = 5 (see backend MILESTONE_PHRASES)
}

const CONFETTI_PRESETS = {
  streak: { spread: 80, colors: ['#FF6B1A', '#F4B740', '#FF7A45', '#2EC4B6'] },
  reward: { spread: 100, colors: ['#F4B740', '#FF6B1A', '#D97398', '#5B8DEF', '#4A9B7F'] },
  perfect: { spread: 60, colors: ['#2EC4B6', '#4A9B7F', '#FFFFFF'] },
};

function fireConfetti(kind) {
  const preset = CONFETTI_PRESETS[kind] || CONFETTI_PRESETS.streak;
  // Two staggered bursts from bottom-left and bottom-right for a rich feel
  // without a heavy library.
  confetti({
    particleCount: 80,
    startVelocity: 45,
    spread: preset.spread,
    origin: { x: 0.2, y: 0.9 },
    colors: preset.colors,
    scalar: 1.1,
    ticks: 200,
  });
  setTimeout(() => confetti({
    particleCount: 80,
    startVelocity: 45,
    spread: preset.spread,
    origin: { x: 0.8, y: 0.9 },
    colors: preset.colors,
    scalar: 1.1,
    ticks: 200,
  }), 150);
}

// Iter-52.34 — module-level ref for the currently-playing milestone
// audio. The review screen's "Hear what I said" button (which plays
// the child's OWN recording) needs to be able to silence a still-
// running celebration voice so parents don't hear the celebration
// AND the child's playback at the same time. Prior to iter-52.34
// the celebration Audio element was scoped only to `celebrateMilestone`
// and nothing outside this module could cancel it — the parent got
// two overlapping voices on the scoring screen.
let _currentCelebrationAudio = null;

/**
 * Silences whatever celebration voice is currently playing (if any).
 * Safe to call at any time — no-op when nothing is playing. Used by
 * the review screen's "Hear what I said" button so the child's own
 * recording plays cleanly, and by any other flow that needs to
 * guarantee no residual celebration is bleeding into new audio.
 */
export function stopCelebrationAudio() {
  const a = _currentCelebrationAudio;
  if (!a) return;
  try { a.pause(); } catch { /* noop */ }
  try { a.currentTime = 0; } catch { /* noop */ }
  try { URL.revokeObjectURL(a.src); } catch { /* noop */ }
  _currentCelebrationAudio = null;
}

/**
 * Celebrate a milestone — fires confetti immediately and plays the
 * cached voice line as soon as it arrives (no await-blocking so the
 * reading flow never stalls). Returns the Audio element so callers can
 * cancel/mute it if the user navigates away.
 *
 * Iter-52.16 — added `silent` opt. When multiple milestones fire for
 * the same score event, the caller now voices only the FIRST and
 * passes `silent: true` for the rest to avoid two ElevenLabs voices
 * overlapping (English Starter Library review bug). Confetti still
 * fires either way so the visual burst is unchanged.
 *
 * Iter-52.34 — the audio element is now tracked at module scope so
 * external callers can stop it via `stopCelebrationAudio()`.
 */
export async function celebrateMilestone({ kind, language = 'en', voiceGender = 'female', silent = false }) {
  fireConfetti(kind);
  if (silent) return null;
  const idx = nextIdx(kind, language);
  const blob = await fetchMilestoneAudio({ kind, idx, language, voiceGender });
  if (!blob) return null;
  // Cancel any prior celebration still ringing before starting the new
  // one — guards against back-to-back milestones stacking voices.
  stopCelebrationAudio();
  const audio = new Audio(URL.createObjectURL(blob));
  _currentCelebrationAudio = audio;
  audio.play().catch(() => {});
  audio.onended = () => {
    try { URL.revokeObjectURL(audio.src); } catch { /* noop */ }
    if (_currentCelebrationAudio === audio) _currentCelebrationAudio = null;
  };
  return audio;
}
